import React from 'react';
import { CheckCircle, ExternalLink } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { getMerchantHeaderClasses, getMerchantButtonClasses } from '../utils/merchantColors';
import { t, pickConfigText, resolveConfigText } from '../utils/i18n';

/**
 * Dynamic Thank You Component
 *
 * Displays:
 * - Success message
 * - Optional review request with location-specific Google Maps URL
 * - Up to 3 configurable instruction boxes
 */
const DynamicThankYou = ({
  config,
  language = 'de',
  merchant = null,
  googleMapsUrl = null,
  locationId = null
}) => {
  // Header/message: exact language or built-in generic; review and box texts
  // have no built-in equivalent, so they fall back across languages below.
  const headerText = pickConfigText(config?.header_text, language) || t(language, 'thankYouTitle');
  const message = pickConfigText(config?.message, language) || t(language, 'thankYouMessage');
  const reviewRequest = config?.review_request || {};
  const instructionBoxes = config?.instruction_boxes || [];

  // A box with no location_ids is shown everywhere. A box with a non-empty
  // location_ids is shown only when the current booking's location matches.
  // If we don't know the location (locationId is null), only global boxes show.
  const visibleBoxes = instructionBoxes
    .filter(box => {
      const ids = box?.location_ids;
      if (!Array.isArray(ids) || ids.length === 0) return true;
      return locationId != null && ids.includes(locationId);
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .slice(0, 3);

  // Get icon component by name
  const getIconComponent = (iconName) => {
    if (!iconName) return null;

    // Capitalize first letter for icon component name
    const componentName = iconName.charAt(0).toUpperCase() + iconName.slice(1);
    const IconComponent = LucideIcons[componentName];

    return IconComponent || null;
  };

  // Get color classes
  const getColorClasses = (color) => {
    const colorMap = {
      green: 'bg-green-100 border-green-500 text-green-800',
      blue: 'bg-blue-100 border-blue-500 text-blue-800',
      red: 'bg-red-100 border-red-500 text-red-800',
      orange: 'bg-orange-100 border-orange-500 text-orange-800',
      purple: 'bg-purple-100 border-purple-500 text-purple-800',
      pink: 'bg-pink-100 border-pink-500 text-pink-800',
      yellow: 'bg-yellow-100 border-yellow-500 text-yellow-800',
      gray: 'bg-gray-100 border-gray-500 text-gray-800'
    };

    return colorMap[color] || colorMap.green;
  };

  const handleReviewClick = () => {
    const url = reviewRequest.use_location_specific_url && googleMapsUrl
      ? googleMapsUrl
      : reviewRequest.fallback_url;

    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className={`${getMerchantHeaderClasses(merchant)} p-6`}>
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="w-16 h-16" />
          </div>
          <h1 className="text-3xl font-bold text-center">{headerText}</h1>
          <p className="mt-3 text-center opacity-90 text-lg">{message}</p>
        </div>

        {/* Content */}
        <div className="p-6">
        {/* Review Request */}
        {reviewRequest.enabled && (
          <div className="mb-6 p-6 bg-white rounded-lg shadow-md border border-gray-200">
            <p className="text-gray-700 mb-4 text-center">
              {resolveConfigText(reviewRequest.text, language)}
            </p>
            <button
              onClick={handleReviewClick}
              className={`w-full ${getMerchantButtonClasses(merchant)} px-6 py-3 rounded-lg font-semibold flex items-center justify-center`}
            >
              {resolveConfigText(reviewRequest.button_text, language)}
              <ExternalLink className="w-5 h-5 ml-2" />
            </button>
          </div>
        )}

        {/* Instruction Boxes */}
        {visibleBoxes.length > 0 && (
          <div className="space-y-4">
            {visibleBoxes.map((box, index) => {
              const IconComponent = getIconComponent(box.icon);
              const colorClasses = getColorClasses(box.color);

              return (
                <div
                  key={index}
                  className={`p-5 rounded-lg border-l-4 ${colorClasses}`}
                >
                  <div className="flex items-start">
                    {IconComponent && (
                      <IconComponent className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      {resolveConfigText(box.title, language) && (
                        <h3 className="font-bold text-lg mb-2">
                          {resolveConfigText(box.title, language)}
                        </h3>
                      )}
                      {resolveConfigText(box.text, language) && (
                        <p className="opacity-90">
                          {resolveConfigText(box.text, language)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Default message if no boxes */}
        {visibleBoxes.length === 0 && !reviewRequest.enabled && (
          <div className="text-center text-gray-500 py-12">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <p className="text-lg">
              {t(language, 'returnRecorded')}
            </p>
          </div>
        )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 text-center text-gray-600 text-sm bg-gray-50">
          {t(language, 'closeWindow')}
        </div>
      </div>
    </div>
  );
};

export default DynamicThankYou;
